import { Select, Snack, Snackbar, TextField } from '@nike/eds';
import { useInfiniteQuery } from '@tanstack/react-query';
import Loader from 'components/Loader';
import ProductWallItem from 'components/NewProductWall/ProductWallItem';
import { QUERY_KEYS } from 'constants/index';
import { useAtom } from 'jotai';
import { Fragment, useEffect, useMemo, useState } from 'react';
import { useDebounce } from 'use-debounce';
import { useInView } from 'react-intersection-observer';
import { useRevalidator } from 'react-router-dom';
import {
  productWallErrorMessageAtom,
  productWallHasErrorAtom,
} from '../../atoms/productwall';
import newProductWallClient from '../../clientAPI/newproductwall';
import { ProductWallGroupingResponse } from '../../types/newproductwall';
import propagate403Error from '../../utils/propagate403Error';
import { channelOptions } from '../../utils/rankingRuleHelpers';

const NIKE_COM_CHANNEL_ID = 'd9a5bc42-4b9c-4976-858a-f159cf99c647';

export default function PreviewProductWall({
  ruleId,
  ruleStatus,
  ruleStartDate,
  ruleEndDate,
  ruleTrigger,
}: {
  ruleId: string;
  ruleStatus: string;
  ruleStartDate: string;
  ruleEndDate: string;
  ruleTrigger: RankingRule['trigger'];
}) {
  const revalidator = useRevalidator();

  const [nextMsg, setNextMsg] = useState('');
  const [consumerChannelId, setConsumerChannelId] = useState(
    channelOptions[0].value,
  );

  const isApplyToAll = ruleTrigger.applyToAll;

  const triggerSearchTerm = useMemo(() => {
    if (isApplyToAll) return '';
    const keywords = ruleTrigger.queries?.keywords || [];
    return keywords.length > 0 ? keywords[0].searchTerm : '';
  }, [ruleTrigger, isApplyToAll]);

  const [customSearchTerms, setCustomSearchTerms] = useState(triggerSearchTerm);
  const [debouncedSearch] = useDebounce(customSearchTerms, 500);
  const searchTerms = debouncedSearch;

  const canPreview =
    ruleStatus === 'ACTIVE' && new Date(ruleEndDate).valueOf() > Date.now();

  const previewDate = useMemo(() => {
    const now = Date.now();
    const start = new Date(ruleStartDate).valueOf();
    const end = new Date(ruleEndDate).valueOf();
    const isActive = now >= start && now <= end;
    if (isActive) return undefined;
    const midpoint = new Date(start + (end - start) / 2);
    return midpoint.toISOString().replace(/\.\d{3}Z$/, 'Z');
  }, [ruleStartDate, ruleEndDate]);

  const [errorMsg, setErrorMsg] = useAtom(productWallErrorMessageAtom);
  const [hasError, setHasError] = useAtom(productWallHasErrorAtom);

  const isNikeCom = consumerChannelId === NIKE_COM_CHANNEL_ID;

  const {
    data,
    isFetching,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    error,
  } = useInfiniteQuery({
    queryKey: [
      QUERY_KEYS.RANKING_RULE_PREVIEW_PWALL,
      searchTerms,
      consumerChannelId,
      ruleId,
      ruleStartDate,
      ruleEndDate,
    ],
    getNextPageParam: (lastPage: ProductWallGroupingResponse) =>
      lastPage.pages?.next || undefined,
    queryFn: async ({ pageParam = '' }) => {
      return newProductWallClient.loadNextPage({
        anchor: 0,
        count: 24,
        searchTerms: isNikeCom ? null : searchTerms,
        path: isNikeCom && searchTerms ? `/w?q=${searchTerms}` : undefined,
        language: 'zh-Hans',
        marketplace: 'CN',
        channelId: consumerChannelId,
        queryType: 'PRODUCTS',
        sort: 'featured',
        headerCallerId: 'Search360UI',
        headerUserAgent: 'Search360UI',
        previewDate: previewDate,
        nextUrl: pageParam,
        // epoch seconds to bypass upstream cache
        options: { t: Math.floor(Date.now() / 1000) },
      });
    },
    enabled: canPreview && !!searchTerms,
    cacheTime: 0,
    refetchOnWindowFocus: false,
    onSuccess: () => {
      setNextMsg('No more data');
    },
    onError: (err: keyable) => {
      setErrorMsg(err.message || JSON.stringify(err));
      setHasError(true);
      setNextMsg('Error');
      err.status === 401 && revalidator.revalidate();
    },
  });
  propagate403Error(error as keyable);

  const { ref, inView } = useInView();
  useEffect(() => {
    if (inView) {
      fetchNextPage();
    }
  }, [inView, fetchNextPage]);

  return (
    <div>
      <div className=''>
        <h2 className='eds-type--title-5 tw-mb-1'>Rule Preview</h2>
        {canPreview && (
          <p className='tw-text-sm tw-text-gray-500 tw-mb-4'>
            Preview how products will be ranked when this rule is active. Future
            rules are simulated at a date within the rule&apos;s time range.
          </p>
        )}
        <div className='tw-flex tw-items-center tw-gap-6 tw-mb-4 tw-text-base'>
          <TextField
            id='product-ranking-rule-preview-search-terms'
            label=''
            aria-label='Search Terms'
            placeholder='Enter search terms to preview'
            value={customSearchTerms}
            onChange={(e) => setCustomSearchTerms(e.target.value)}
          />
          <div className='tw-w-56'>
            <Select
              id='product-ranking-rule-preview-channel-select'
              label=''
              aria-label='Channel'
              options={channelOptions}
              value={channelOptions.find((v) => v.value === consumerChannelId)}
              onChange={(event) => {
                setConsumerChannelId(event?.value || '');
              }}
            />
          </div>
        </div>
        {!canPreview && (
          <p className='tw-text-sm tw-text-gray-500'>
            {ruleStatus !== 'ACTIVE'
              ? `Preview is unavailable because the rule status is "${ruleStatus}". Only ACTIVE rules can be previewed.`
              : 'Preview is unavailable because the rule has already expired.'}
          </p>
        )}
        {canPreview && !!searchTerms && (
          <div className='tw-flex tw-flex-wrap tw-gap-5 tw-justify-center'>
            {data?.pages.map((page) => {
              return (
                <Fragment key={page.pages?.next}>
                  {page.productGroupings?.map((p, index) => (
                    <ProductWallItem
                      key={`${page.pages?.next}-${index}`}
                      data={p}
                    />
                  ))}
                </Fragment>
              );
            })}
            {(isFetching || isFetchingNextPage) && (
              <div className='tw-w-full tw-flex tw-justify-center tw-items-center'>
                <Loader />
              </div>
            )}
            <div className='tw-h-2' ref={ref} />
            {!hasNextPage && !isFetchingNextPage && !isFetching && (
              <div className='tw-w-full tw-flex tw-justify-center tw-items-center'>
                <p className='tw-text-gray-500'>{nextMsg}</p>
              </div>
            )}
          </div>
        )}
      </div>
      <Snackbar style={{ left: '90%', top: '90%' }}>
        {errorMsg && (
          <Snack
            autoDismissDuration={5000}
            id='pw-error-notification'
            onDismiss={(_id) => {
              setErrorMsg('');
            }}
            status={hasError ? 'error' : 'success'}
          >
            {errorMsg}
          </Snack>
        )}
      </Snackbar>
    </div>
  );
}
